import {
  Flex,
  Text,
  Link,
  Button,
  Badge,
  Card,
  AudienceIcon,
  Heading,
  CriticalIcon,
  AllChannelsIcon,
  Skeleton,
} from '@buffer-mono/popcorn'
import type { ResultOf } from '~publish/graphql'
import styles from './PlanContent.module.css'
import { getUsersCopy } from './BillingSettings.helpers'
import { HC_UTM_PARAMS } from '~publish/legacy/utils/contants'
import type { AccountForBillingQuery } from '../Billing'
import PlanCard from './PlanCard'

/**
 * Renders the plan card for Mobile Billing.
 * @param data - The billing data to render.
 * @returns The rendered plan card.
 */
export const MobileBilling = ({
  data,
}: {
  data?: ResultOf<typeof AccountForBillingQuery> | null
}): JSX.Element => {
  const organization = data?.account?.currentOrganization
  const billing = organization?.billing

  if (
    !organization ||
    billing?.__typename !== 'OBBilling' ||
    billing.gateway?.gatewayPlatform === 'web'
  ) {
    return <Skeleton show={true} />
  }

  const { subscription, gateway } = billing

  // Channel count and limits
  const connectedChannelsCount = organization.channels.length ?? 0
  const lockedChannelsCount =
    organization.channels.filter((channel) => {
      return channel.isLocked
    }).length ?? 0
  const channelsLimit = organization.limits.channels

  // Billing states
  const isOnTrial = subscription.trial?.isActive ?? false

  const renewalDate = subscription.periodEnd
    ? new Date(subscription.periodEnd).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  const canceledDate = subscription.canceledAt
    ? new Date(subscription.canceledAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  const gatewayStoreName =
    gateway.gatewayPlatform === 'android'
      ? 'Google Play Store'
      : gateway.gatewayPlatform === 'apple'
      ? 'Apple App Store'
      : 'Buffer App'

  const usersCopy = getUsersCopy(subscription.plan.limits.members ?? 0)

  const handleChangePlan = (): void => {
    const { MODALS, actions } = window?.appshell || {}
    actions.openModal(MODALS.planSelector, {
      cta: 'billing-plansCard-changePlanFromMobile-1',
    })
  }

  return (
    <Flex direction="column" gap="md" fullWidth>
      {/* Header */}
      <Flex direction="row" fullWidth>
        {/* Plan details */}
        <Flex direction="column" gap="xs" fullWidth>
          {/* Plan name and trial status */}
          <Flex direction="row" gap="xs" align="center">
            <Heading size="medium">Plan:</Heading>
            <Badge
              size="mid"
              variant="brand-subtle"
              style={{ textTransform: 'uppercase' }}
            >
              {subscription.plan.name}
            </Badge>
            {isOnTrial && (
              <Badge
                size="mid"
                variant="neutral"
                style={{ textTransform: 'uppercase' }}
              >
                On Trial
              </Badge>
            )}
          </Flex>
          {/* Paid plans canceled at period end */}
          {subscription.isCanceledAtPeriodEnd && (
            <li>
              <Text size="md">
                {canceledDate && `Your plan was changed on ${canceledDate}. `}
                You can continue using the{' '}
                <strong>{subscription.plan.name}</strong> features until the end
                of your billing cycle on <strong>{renewalDate}</strong>, when
                your Free subscription starts.
              </Text>
            </li>
          )}
          {/* Renewal date */}
          {!subscription.isCanceledAtPeriodEnd &&
            subscription.plan.id !== 'free' && (
              <ul className={styles.planCardList}>
                <li>
                  <Text size="md">
                    Billed {subscription.plan.interval}ly. Renews on{' '}
                    <strong>{renewalDate}</strong>
                  </Text>
                </li>
              </ul>
            )}
        </Flex>
        {/* Actions */}
        <Flex direction="row" gap="2xs" align="center">
          <Button size="medium" variant="secondary" onClick={handleChangePlan}>
            Change Plan
          </Button>
        </Flex>
      </Flex>
      {/* 2-column plan details card */}
      <Card className={styles.planCard}>
        <Flex
          direction="row"
          align="center"
          justify="around"
          className={styles.statCardContainer}
        >
          {/* Users */}
          <PlanCard>
            <PlanCard.Icon>
              <AudienceIcon />
            </PlanCard.Icon>
            <PlanCard.Title>Users</PlanCard.Title>
            <PlanCard.Info>{usersCopy}</PlanCard.Info>
            <PlanCard.Value>{organization?.members?.totalCount}</PlanCard.Value>
          </PlanCard>

          {/* Channels */}
          <PlanCard>
            <PlanCard.Icon>
              <AllChannelsIcon />
            </PlanCard.Icon>
            <PlanCard.Title>Channels</PlanCard.Title>
            <PlanCard.Info>
              {subscription.plan.interval === 'year'
                ? `$${subscription.plan.prices.basePlanPrice} each per year`
                : `$${subscription.plan.prices.baseMonthlyPrice} each per month`}
            </PlanCard.Info>
            <PlanCard.Value>
              {isOnTrial
                ? `${connectedChannelsCount}`
                : `${connectedChannelsCount}/${channelsLimit}`}
            </PlanCard.Value>
            {lockedChannelsCount > 0 && (
              <PlanCard.FootNote>
                <CriticalIcon color="critical" />
                <Text size="sm" color="critical">
                  {lockedChannelsCount} channel
                  {lockedChannelsCount !== 1 ? 's' : ''} locked
                </Text>
              </PlanCard.FootNote>
            )}
          </PlanCard>
        </Flex>
      </Card>
      <Flex direction="column" align="center" gap="lg" fullWidth>
        <Text size="md" align="center" weight="bold" lineHeight="regular">
          Your subscription is managed through the {gatewayStoreName}.{' '}
          <Link
            target="_blank"
            href={`https://support.buffer.com/hc/en-us/articles/360038768454?${HC_UTM_PARAMS}`}
          >
            Learn more
          </Link>
        </Text>
        <Text size="md" align="center">
          Need help?{' '}
          <Link
            onClick={(): void => {
              window.Beacon?.('navigate', '/ask/message/')
            }}
          >
            Talk to our support team
          </Link>
          .
        </Text>
      </Flex>
    </Flex>
  )
}
